import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { UsFaq, US_FAQ } from "@/components/UsFaq";
import {
  ShieldCheck,
  Truck,
  RotateCcw,
  Lock,
  Globe2,
} from "lucide-react";
import { trackHeroCta, trackRailInteraction } from "@/lib/gtag";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  PRODUCTS_QUERY,
  storefrontApiRequest,
  type ShopifyProduct,
} from "@/lib/shopify";
import { US_MARKET_ENABLED } from "@/lib/markets";

/** Head for the paused US storefront: not indexed, no commercial claims. */
const PAUSED_HEAD = {
  meta: [
    { name: "robots", content: "noindex, nofollow" },
    { title: "Montessori24 USA" },
    {
      name: "description",
      content:
        "US ordering is not currently available. Montessori24 currently serves customers in Finland.",
    },
    { property: "og:title", content: "Montessori24 USA" },
    { property: "og:description", content: "US ordering is not currently available." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ],
  links: [{ rel: "canonical", href: "https://montessori24.fi/us" }],
};
// @ts-expect-error vite-imagetools transforms query at build time
import heroAvif from "@/assets/lifestyle-pikler.jpg?format=avif&w=1200&quality=72";
// @ts-expect-error vite-imagetools transforms query at build time
import heroAvifSm from "@/assets/lifestyle-pikler.jpg?format=avif&w=720&quality=62";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWebp from "@/assets/lifestyle-pikler.jpg?format=webp&w=1200&quality=78";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWebpSm from "@/assets/lifestyle-pikler.jpg?format=webp&w=720&quality=78";

const featuredQueryOptions = queryOptions({
  queryKey: ["products", "us-featured", "US", "EN"] as const,
  queryFn: async () => {
    try {
      const res = await storefrontApiRequest(PRODUCTS_QUERY, {
        first: 12,
        query: "pikler OR kiipeily OR kolmio OR tower OR iglu",
        country: "US",
        language: "EN",
      });
      return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
    } catch (err) {
      console.error("[us] featured rail fetch failed:", err);
      return [] as ShopifyProduct[];
    }
  },
});

export const Route = createFileRoute("/us")({
  loader: async ({ context }) => {
    // US ordering is paused — don't fetch a storefront we don't render.
    if (!US_MARKET_ENABLED) return;
    await context.queryClient.ensureQueryData(featuredQueryOptions);
  },
  head: () =>
    !US_MARKET_ENABLED
      ? PAUSED_HEAD
      : {
    meta: [
      // The US market is not actively operated — see src/lib/markets.ts.
      // The page stays reachable by direct URL but must not be indexed.
      { name: "robots", content: "noindex, follow" },
      { title: "Montessori Furniture from European Makers | Montessori24" },
      {
        name: "description",
        content:
          "Pikler triangles, learning towers and Montessori furniture from selected European manufacturers, curated by a Finnish specialist retailer. US orders are currently not available.",
      },
      { property: "og:title", content: "Montessori Furniture from European Makers" },
      {
        property: "og:description",
        content:
          "Pikler triangles and Montessori furniture from selected European manufacturers, curated by a Finnish specialist retailer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Montessori24" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "fi_FI" },
      { property: "og:url", content: "https://montessori24.fi/us" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Pikler climbing frame in a bright nursery" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Montessori Furniture from European Makers" },
      {
        name: "twitter:description",
        content:
          "Pikler triangles and Montessori furniture from selected European manufacturers, curated by a Finnish specialist retailer.",
      },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://montessori24.fi/us" },
      {
        rel: "preload",
        as: "image",
        href: heroAvifSm,
        imageSrcSet: `${heroAvifSm} 720w, ${heroAvif} 1200w`,
        imageSizes: "(min-width: 768px) 50vw, 100vw",
        type: "image/avif",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Montessori Furniture from European Makers",
          url: "https://montessori24.fi/us",
          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            name: "Montessori24",
            url: "https://montessori24.fi",
          },
          about: {
            "@type": "Organization",
            name: "Montessori24",
            url: "https://montessori24.fi",
            areaServed: { "@type": "Country", name: "United States" },
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://montessori24.fi/" },
              { "@type": "ListItem", position: 2, name: "United States", item: "https://montessori24.fi/us" },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "en-US",
          mainEntity: US_FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  },
  component: UsLanding,
});

function UsLanding() {
  if (!US_MARKET_ENABLED) return <UsPaused />;
  return <UsStorefront />;
}

/**
 * Shown while US ordering is disabled. No pricing, shipping, delivery or
 * checkout claims and no product grid — just an honest status message.
 */
function UsPaused() {
  return (
    <Layout>
      <section className="container-prose px-4 py-24 md:px-6 md:py-32">
        <div className="max-w-xl">
          <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
            Montessori24 USA
          </p>
          <h1 className="mt-4 font-display text-4xl italic leading-tight text-foreground md:text-5xl">
            US ordering is not currently available.
          </h1>
          <p className="mt-6 text-sm leading-relaxed text-muted-foreground md:text-base">
            Montessori24 currently serves customers in Finland. US ordering may
            return in the future.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-foreground px-7 py-3.5 text-[11px] font-medium uppercase tracking-[0.22em] text-background transition-transform hover:-translate-y-0.5"
          >
            Visit Montessori24 Finland <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </Layout>
  );
}

function UsStorefront() {
  const navigate = useNavigate();
  const search = useRouterState({
    select: (s) => s.location.search as Record<string, unknown>,
  });

  // Force the US market context on this page so Shopify returns USD pricing
  // and cart/checkout use the US buyer identity. Preserves other params.
  useEffect(() => {
    if (search.market !== "us") {
      navigate({
        to: "/us",
        search: { ...search, market: "us" },
        replace: true,
      });
    }
  }, [navigate, search]);

  const { data: featured } = useSuspenseQuery(featuredQueryOptions);
  const rail = "us_featured";

  return (
    <Layout>
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-cream">
        <div className="container-prose px-4 pt-6 pb-16 md:px-6 md:pt-10 md:pb-24">
          <div className="relative overflow-hidden rounded-sm md:rounded-md">
            <div className="relative aspect-[4/5] w-full bg-sand md:aspect-[16/10] lg:aspect-[21/10]">
              <picture>
                <source
                  type="image/avif"
                  srcSet={`${heroAvifSm} 720w, ${heroAvif} 1200w`}
                  sizes="100vw"
                />
                <source
                  type="image/webp"
                  srcSet={`${heroWebpSm} 720w, ${heroWebp} 1200w`}
                  sizes="100vw"
                />
                <img
                  src={heroWebpSm}
                  alt="Toddler climbing a wooden Pikler triangle in a bright, minimalist nursery"
                  width={1600}
                  height={1000}
                  fetchPriority="high"
                  decoding="async"
                  className="absolute inset-0 block h-full w-full object-cover"
                />
              </picture>
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 md:bg-gradient-to-r md:from-black/70 md:via-black/35 md:to-transparent"
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 md:justify-center md:p-16 lg:p-24">
                <div className="max-w-xl md:max-w-2xl">
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-medium uppercase tracking-[0.24em] text-white/85">
                    <span>Finnish specialist retailer</span>
                    <span aria-hidden className="h-px w-4 bg-white/40" />
                    <span>Finnish store · Made in Europe 🇪🇺</span>
                  </p>
                  <h1 className="mt-4 font-display text-4xl italic leading-[1.02] tracking-[-0.01em] text-white text-balance md:mt-6 md:text-6xl lg:text-7xl">
                    European Montessori products,{" "}
                    <span className="not-italic">selected by a Finnish retailer.</span>
                  </h1>
                  <p className="mt-5 max-w-lg text-sm leading-relaxed text-white/85 md:mt-6 md:text-base">
                    Climbing frames, learning towers and Montessori furniture
                    from selected European manufacturers. US ordering is
                    currently paused — browse the range and contact us for
                    availability.
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3 md:mt-9">
                    <Link
                      to="/kauppa"
                      search={{ market: "us" }}
                      className="inline-flex items-center gap-3 border border-white/40 bg-white px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-foreground transition-all hover:-translate-y-0.5"
                      data-cta="us_hero_shop"
                      onClick={() =>
                        trackHeroCta({
                          variant: "US",
                          destination: "/kauppa?market=us",
                          label: "Shop the collection",
                        })
                      }
                    >
                      Shop the collection <span aria-hidden>→</span>
                    </Link>
                    <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-white/80">
                      From $169 · Free US shipping
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute top-5 right-5 hidden items-center gap-2 border border-white/25 bg-white/10 px-3 py-1.5 backdrop-blur-md md:flex">
                <ShieldCheck className="h-3.5 w-3.5 text-white" strokeWidth={1.5} />
                <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-white">
                  Product-specific safety info
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* US TRUST BADGES */}
      <section className="border-y border-border bg-sand/40">
        <div className="container-prose px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {[
              {
                Icon: Truck,
                title: "Free US shipping",
                body: "6–10 business days from Finland. Tracked, door-to-door.",
              },
              {
                Icon: RotateCcw,
                title: "30-day returns",
                body: "Not the right fit? Send it back within 30 days.",
              },
              {
                Icon: ShieldCheck,
                title: "Product-specific safety info",
                body: "Product-specific safety and compliance details on each product page.",
              },
              {
                Icon: Lock,
                title: "Secure USD checkout",
                body: "Shop Pay · Apple Pay · Visa · Mastercard · Amex.",
              },
            ].map(({ Icon, title, body }) => (
              <div
                key={title}
                className="flex flex-col gap-2 rounded-2xl border border-border bg-cream p-5 md:p-6"
              >
                <Icon className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                <div className="text-sm font-medium text-foreground">{title}</div>
                <div className="text-xs leading-relaxed text-muted-foreground">{body}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[10px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Globe2 className="h-3 w-3" /> Duties & taxes calculated at checkout
            </span>
          </div>
        </div>
      </section>

      {/* US-FOCUSED FEATURED COLLECTION */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose px-4 py-16 md:px-6 md:py-24">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
                Featured for the US
              </p>
              <h2 className="mt-2 font-display text-3xl italic tracking-[-0.01em] text-foreground md:text-5xl">
                Bestsellers, in USD.
              </h2>
              <p className="mt-3 max-w-lg text-sm text-muted-foreground md:text-base">
                A selection of climbers and Montessori pieces from European
                manufacturers. Availability and delivery are confirmed per
                product.
              </p>
            </div>
            <Link
              to="/kauppa"
              search={{ market: "us" }}
              className="hidden shrink-0 text-[11px] font-medium uppercase tracking-[0.22em] text-foreground underline-offset-4 hover:underline md:inline"
              onClick={() =>
                trackRailInteraction({ rail, action: "view_all" })
              }
            >
              Shop all →
            </Link>
          </div>

          {featured.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-dashed border-border bg-sand/40 p-10 text-center text-sm text-muted-foreground">
              No products found. Please check back soon.
            </div>
          ) : (
            <div className="mt-10">
              <Carousel opts={{ align: "start", loop: false }}>
                <CarouselContent className="-ml-4">
                  {featured.map((p, i) => (
                    <CarouselItem
                      key={p.node.id}
                      className="basis-[80%] pl-4 sm:basis-1/2 lg:basis-1/3"
                    >
                      <ProductCard
                        product={p}
                        railName={rail}
                        railPosition={i}
                        sizes="(min-width: 1024px) 32vw, (min-width: 640px) 45vw, 80vw"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="mt-6 hidden justify-end gap-2 md:flex">
                  <CarouselPrevious
                    className="static translate-y-0"
                    onClick={() =>
                      trackRailInteraction({ rail, action: "scroll" })
                    }
                  />
                  <CarouselNext
                    className="static translate-y-0"
                    onClick={() =>
                      trackRailInteraction({ rail, action: "scroll" })
                    }
                  />
                </div>
              </Carousel>
            </div>
          )}

          <div className="mt-8 flex justify-center md:hidden">
            <Link
              to="/kauppa"
              search={{ market: "us" }}
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background"
              onClick={() =>
                trackRailInteraction({ rail, action: "view_all" })
              }
            >
              Shop all →
            </Link>
          </div>
        </div>
      </section>

      <UsFaq />
    </Layout>
  );
}
